import axios from "axios";
import { BASE_URL } from "../constants/applicationConfig";
import { logger } from "../utils/logger";
import { RefreshTokenApi } from "./RefreshTokenApi";

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

function logFailure(error) {
  const correlationId =
    error.response?.headers?.["x-correlation-id"] ||
    error.response?.data?.correlationId ||
    error.config?.metadata?.correlationId;

  const duration = Date.now() - (error.config?.metadata?.startTime || 0);

  // Log the error with correlation ID and context
  logger.error("API Request Failed", error, {
    correlationId,
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    status: error.response?.status,
    duration: `${duration}ms`,
    errorCode: error.response?.data?.error,
  });
}

let _token = null;
export const setAccessToken = (newToken) => {
  _token = newToken;
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (_token) {
      config.headers.Authorization = `Bearer ${_token}`;
    }

    const correlationId = crypto.randomUUID();
    config.headers["X-Correlation-ID"] = correlationId;
    config.metadata = { correlationId, startTime: Date.now() };

    logger.debug("API Request", {
      correlationId,
      method: config.method?.toUpperCase(),
      url: config.url,
    });

    return config;
  },
  (error) => {
    logger.error("Request setup failed", error);
    return Promise.reject(error);
  },
);

// axiosInstance.js

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    logFailure(error);

    const originalRequest = error.config;

    // 1. Check if the failed request WAS the refresh call itself
    // We don't want to attempt a refresh-of-a-refresh!
    const isRefreshCall = originalRequest.url.includes("/api/refresh-token");

    // Only attempt silent refresh if it's a 401 AND not the refresh call itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshCall
    ) {
      originalRequest._retry = true;
      try {
        const rs = await RefreshTokenApi.refresh();
        const { accessToken } = rs.data;

        setAccessToken(accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (_error) {
        // Only redirect if the refresh loop fails
        setAccessToken(null);
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(_error);
      }
    }

    // 2. If the refresh call itself failed with a 401, just reject it
    // This allows AuthContext to catch it and set isAuthenticated to false quietly
    return Promise.reject(error);
  },
);

export default axiosInstance;
